/**
 * QUIL — Auth context
 * Wraps MSAL.js for use throughout the app.
 * Phase 3 PoC — Phase 4 will extend with sync triggers.
 */

import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { AccountInfo } from '@azure/msal-browser'
import { msalInstance, msalReady, GRAPH_SCOPES, acquireBoundToken } from './auth'
import type { TokenGetter } from './authToken'
import { clearSignedOut, markReturningVisitor, markSignedOut } from './authFlags'
import { markInteractiveSignIn } from './firstBoot'
import { initSyncListeners } from './sync'
import { connectGraph, forgetGraphRoot } from './onedrive'
import { db, ensureSettings } from './db'
import { AuthContext } from './authContext'
import type { StoreState } from './authContext'
import { defaultStoreResolver } from './storeBinding'
import type { BoundStore, StoreResolver } from './storeBinding'
import { FIRST_BOOT_KEY } from './scopeKeys'

// Silent-or-throw, and it NEVER opens a popup. It used to answer an interaction-required error
// with acquireTokenPopup, and the 2026-09-17 flow-coherence review (#8) found why that is wrong
// here: the 30-second upload drain in sync.ts calls this with no click in the loop, and
// sync.test.ts proves that timer fires with zero user events. Browsers block popups outside a
// gesture, and a blocked popup matched SYNC_PATTERNS, so sync stopped with only a generic error.
//
// Both failure modes report InteractiveSignInRequiredError, which isAuthInteractionError
// recognises by type, so the queue pauses without burning retries. Recovery is a click.
//
// W10: ONE getter, built at module load and bound to the shared Graph client here, so it is in
// place before any effect runs (AuthGate is a child, and its cold-start effect fires before this
// provider's). It passes { forceRefresh } through, which is what lets the client answer a first 401
// with one refreshed retry. It waits for msalReady, so a call that races MSAL's initialisation is not
// misread as a failure.
//
// W12 A5: it no longer reads the ACTIVE account. The token comes from the getter bound to the
// OPEN STORE's account (`auth.acquireBoundToken`, set by storeBinding), looked up in MSAL's cache by
// id, so a tab working on A's store can never send B's token however the active account changes. With
// no store bound there is no token at all.
const getAccessToken: TokenGetter = async (options) => {
  await msalReady
  return acquireBoundToken(options)
}
connectGraph(getAccessToken)

export function AuthProvider({ children, resolver = defaultStoreResolver }: { children: ReactNode; resolver?: StoreResolver }) {
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  // The store this tab opened for the account, matched BEFORE anything reads or syncs it. Derived
  // from the key the result was produced for, so a stale result never answers for another account
  // or another attempt, and no setState runs synchronously in an effect.
  const [retryNonce, setRetryNonce] = useState(0)
  const [settled, setSettled] = useState<{ key: string; store: BoundStore | null; error: Error | null } | null>(null)
  const [offline, setOffline] = useState<BoundStore | null>(null)

  useEffect(() => {
    msalReady.then(() => {
      // Prefer the active account (set in msalReady from the cache / redirect result);
      // fall back to the first known account. This is what restores a returning device's
      // session so the sign-in page doesn't reappear on every open.
      const restored = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null
      if (restored) {
        msalInstance.setActiveAccount(restored)
        setAccount(restored)
        // "Have we met before" — gates the cold-start bounce so a brand-new visitor is never
        // thrown at a Microsoft login page before seeing Quil. Set on a COMPLETED sign-in, never
        // on one that merely started. W9: Quil had no such flag and bounced anyone signed out.
        markReturningVisitor()
      }
      setIsInitializing(false)
    })
  }, [])

  // Key the sync-listener effect on the stable account id, not the AccountInfo
  // object — MSAL can hand back a new object for the same user, which would
  // otherwise tear down + re-arm all listeners (and re-fire the initial sync) (F9).
  const accountId = account?.homeAccountId ?? null

  // Match the signed-in account to its store (W12 A5). Nothing below touches the library until this
  // has settled, and a failure commits nothing (quilScope.resolveStore) and offers a retry.
  const storeKey = accountId ? `${accountId}:${retryNonce}` : null
  useEffect(() => {
    if (!storeKey || !accountId) return
    let live = true
    resolver.bind(accountId).then(
      (store) => { if (live) setSettled({ key: storeKey, store, error: null }) },
      (error: unknown) => { if (live) setSettled({ key: storeKey, store: null, error: error instanceof Error ? error : new Error(String(error)) }) },
    )
    return () => { live = false }
  }, [storeKey, accountId, resolver])

  const storeState: StoreState = accountId
    ? settled && settled.key === storeKey
      ? settled.store ? { status: 'ready', store: settled.store } : { status: 'failed', error: settled.error ?? new Error('The library could not be opened.') }
      : { status: 'resolving' }
    : offline ? { status: 'ready', store: offline } : { status: 'none' }
  const store = storeState.status === 'ready' ? storeState.store : null

  const retryStore = useCallback(() => setRetryNonce((n) => n + 1), [])
  const canOpenOffline = useCallback(() => resolver.canOpenOffline(), [resolver])
  const openOffline = useCallback(async () => {
    const opened = await resolver.bindOffline()
    if (opened) setOffline(opened)
    return opened
  }, [resolver])

  useEffect(() => {
    // Not bound yet (or a signed-out visitor with no library open): there is nothing to touch.
    if (!store) return
    // A read-only store is never written and never synced: the sync pill says so.
    if (store.readOnly) return
    if (!accountId) {
      // Reflect the disconnected state so the save indicator reads correctly.
      void ensureSettings().then(() => db.settings.update(1, { oneDriveConnected: false }))
      return
    }
    // The owner stamp is the store's own and is never rewritten by a sign-in: before W12 this line
    // also wrote `oneDriveUserId = accountId`, so a second account's sign-in overwrote the first's.
    void ensureSettings().then(() => db.settings.update(1, { oneDriveConnected: true }))
    const cleanup = initSyncListeners()
    return cleanup
  }, [store, accountId])

  async function login() {
    // Redirect flow: navigates the whole tab to Microsoft and back — no popup/opener,
    // so it can't be severed by COOP (a popup booted the full app and never completed
    // the handshake — P2-1 follow-up). msalReady's handleRedirectPromise consumes the
    // response on return and promotes the account; AuthProvider then restores it.
    await msalReady
    // An explicit sign-in withdraws the standing "I signed out on purpose" instruction, so the
    // cold-start bounce is allowed again from here on.
    clearSignedOut()
    // W12 P2 is "no interactive sign-in since the first boot": this is one (and so is the automatic
    // bounce AuthGate starts through here, which goes through the identity provider all the same).
    markInteractiveSignIn(FIRST_BOOT_KEY)
    await msalInstance.loginRedirect({ scopes: GRAPH_SCOPES })
  }

  async function logout() {
    if (!account) return
    // Recorded BEFORE the redirect: it navigates to Microsoft and comes back into a fresh page
    // load, where AuthGate's cold start reads this flag. Quil had no such flag, so its auto-login
    // (which has no returning-visitor guard either) would sign you straight back in after you
    // chose to leave. W9.
    markSignedOut()
    // The transport's remembered approot (onedrive.ts `rememberRoot`): the next session checks first.
    forgetGraphRoot(account.homeAccountId)
    // Redirect flow to match login — navigates to Microsoft logout and back.
    await msalInstance.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin })
  }

  return (
    <AuthContext.Provider value={{ account, isInitializing, login, logout, getAccessToken, storeState, retryStore, canOpenOffline, openOffline }}>
      {children}
    </AuthContext.Provider>
  )
}
